import PropTypes from 'prop-types'
import R from 'ramda'

import { Box, Icon, Text } from '@redriverpress/system'

import {
  cefr,
  clb,
  eld,
  nrs,
  eslLevels as allEslLevels,
  none,
} from '../../constants/levels'

export default function Levels({
  eslLevels,
  preferredLevels,
  preferredLevelType,
  ...props
}) {
  // Levels should be shown as 'All levels' if the material has been tagged with every level, or no levels
  const shouldBeAllLevels =
    R.equals(eslLevels, allEslLevels) || eslLevels.length === 0

  // This code was adapted from the logic on the BE to correct display the levels on two situations:
  // - with Pre ELD
  // Pre ELD - ELD 3
  // - without Pre ELD
  // ELD 2-3
  const printEldLevelRange = eldLevels => {
    const firstItem = R.head(eldLevels)
    if (firstItem === 'Pre ELD') {
      return R.uniq([firstItem, R.last(eldLevels)]).join('\u202f\u2013\u202f')
    }

    return R.uniq([
      firstItem.replace('ELD ', ''),
      R.last(eldLevels).replace('ELD ', ''),
    ]).join('\u202f\u2013\u202f')
  }

  // Prints the first and last index of a set of levels.
  // If the first and last index of a set of levels are unique,
  // joins them with an en-dash (\u2013) with a narrow non-breaking space (\u202f) on each side of it.
  // If the first and last indexes are not unique, just prints that one index.
  // Adapted from Michael’s code!
  const printLevelRange = levels =>
    R.uniq([R.head(levels), R.last(levels)]).join('\u202f\u2013\u202f')

  const printLevels = () => {
    // User has chosen Ellii levels as their preferred level system,
    // OR user's preferred levels are not available on this item
    if (preferredLevels === eslLevels || !preferredLevels.length) {
      return printLevelRange(eslLevels)
    }

    if (preferredLevelType === 'eld') {
      const firstItem = R.head(preferredLevels)

      return `
      ${firstItem === 'Pre ELD' ? '' : preferredLevelType.toUpperCase()}
      ${printEldLevelRange(preferredLevels)}
      (${printLevelRange(eslLevels)})
    `
    }

    return `
      ${preferredLevelType.toUpperCase()}
      ${printLevelRange(preferredLevels)}
      (${printLevelRange(eslLevels)})
    `
  }

  return (
    <Box
      display='flex'
      alignItems='baseline'
      mb={[2, null, 0]}
      mr={[0, null, 4]}
      {...props}
    >
      <Icon width='1rem' pr={2} flexShrink={0} icon='lightbulb' />
      <Text>{shouldBeAllLevels ? 'All levels' : printLevels()}</Text>
    </Box>
  )
}

Levels.defaultProps = {
  eslLevels: [],
  preferredLevels: [],
}

Levels.propTypes = {
  eslLevels: PropTypes.arrayOf(PropTypes.string),
  preferredLevels: PropTypes.arrayOf(PropTypes.string),
  preferredLevelType: PropTypes.oneOf([cefr, clb, eld, nrs, none]).isRequired,
}
